/** Builds a single PDF ("Recipe Book") containing the full details - ingredients, allergens,
 * and method - for every recipe passed in. Demo Mode: works entirely off the in-memory mock
 * catalogue (`recipeV2Data.ts`), same as the rest of Recipe Bank - no server call, no typed
 * client. Uses `jspdf` directly (already a project dependency); text is laid out manually rather
 * than via a table plugin since `jspdf-autotable` isn't installed and the content here is simple
 * label/value pairs and wrapped paragraphs. */

import { jsPDF } from 'jspdf';
import { AllergenStatus, RecipeV2, RecipeV2Detail } from '../../../app/services/api/recipeV2/recipeV2Types';

const PAGE_MARGIN = 15;
const LINE_HEIGHT = 6;

const addPageIfNeeded = (doc: jsPDF, cursorY: number, linesNeeded = 1): number => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (cursorY + linesNeeded * LINE_HEIGHT > pageHeight - PAGE_MARGIN) {
    doc.addPage();
    return PAGE_MARGIN;
  }
  return cursorY;
};

const writeParagraph = (doc: jsPDF, text: string, y: number, maxWidth: number): number => {
  let cursorY = y;
  const wrapped = doc.splitTextToSize(text, maxWidth);
  wrapped.forEach((line: string) => {
    cursorY = addPageIfNeeded(doc, cursorY);
    doc.text(line, PAGE_MARGIN, cursorY);
    cursorY += LINE_HEIGHT;
  });
  return cursorY;
};

const writeSectionHeading = (doc: jsPDF, title: string, y: number): number => {
  let cursorY = addPageIfNeeded(doc, y, 2);
  cursorY += 2;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(title, PAGE_MARGIN, cursorY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  return cursorY + LINE_HEIGHT;
};

/** Generates the Recipe Book PDF and returns the jsPDF instance (caller decides how to save it). */
export const buildRecipeBookPdf = (recipes: RecipeV2[], detailsById: Record<number, RecipeV2Detail>): jsPDF => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - PAGE_MARGIN * 2;

  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Recipe Book', PAGE_MARGIN, 25);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`${recipes.length} recipes - generated ${new Date().toLocaleDateString()}`, PAGE_MARGIN, 33);

  recipes.forEach((recipe) => {
    const detail = detailsById[recipe.id];
    const displayName = recipe.customerFacingName ?? recipe.description;
    doc.addPage();
    let cursorY = PAGE_MARGIN;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    cursorY = writeParagraph(doc, displayName, cursorY + 5, contentWidth);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    cursorY += 2;
    const summaryLine = `${recipe.primaryCategory} | Yield: ${recipe.yieldQuantity} ${recipe.yieldUom} | Cost per portion: ${recipe.costPerPortion.toFixed(2)}`;
    cursorY = writeParagraph(doc, summaryLine, cursorY, contentWidth);

    if (recipe.shortDescription) {
      cursorY = writeParagraph(doc, recipe.shortDescription, cursorY + 2, contentWidth);
    }

    const ingredients = detail?.ingredients ?? [];
    cursorY = writeSectionHeading(doc, 'Ingredients', cursorY + 2);
    if (ingredients.length === 0) {
      cursorY = writeParagraph(doc, 'No ingredients recorded', cursorY, contentWidth);
    } else {
      ingredients.forEach((ingredient) => {
        const line = `- ${ingredient.name}: ${ingredient.quantity} ${ingredient.unit}${ingredient.preparation ? `, ${ingredient.preparation}` : ''}`;
        cursorY = writeParagraph(doc, line, cursorY, contentWidth);
      });
    }

    cursorY = writeSectionHeading(doc, 'Allergens', cursorY + 2);
    const containsAllergens = recipe.allergenSummary.filter(
      (a) => a.status === AllergenStatus.Contains || a.status === AllergenStatus.MayContain,
    );
    cursorY = writeParagraph(
      doc,
      containsAllergens.length === 0 ? 'None declared' : containsAllergens.map((a) => a.allergenName).join(', '),
      cursorY,
      contentWidth,
    );

    const steps = detail?.steps ?? [];
    cursorY = writeSectionHeading(doc, 'Method', cursorY + 2);
    if (steps.length === 0) {
      cursorY = writeParagraph(doc, 'No method steps recorded', cursorY, contentWidth);
    } else {
      steps.forEach((step) => {
        cursorY = writeParagraph(doc, `${step.stepNumber}. ${step.title}`, cursorY, contentWidth);
        cursorY = writeParagraph(doc, step.instruction, cursorY, contentWidth);
        cursorY += 1;
      });
    }
  });

  return doc;
};
